/**
 * Shared formatting for the performance dashboard.
 *
 * Collected in one place because the same number appears on five tabs, and a duration rendered
 * three different ways on one page makes two panels look like they disagree when they do not.
 */

/**
 * A duration, at a precision that matches its size.
 *
 * Sub-millisecond timings get a decimal place because at that scale the decimal IS the reading;
 * anything over a second is shown in seconds, because "4820ms" makes a reader do arithmetic to
 * find out they are looking at five seconds.
 */
export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return '—'
  if (ms < 1) return `${ms.toFixed(2)}ms`
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
}

/** A count, grouped. Big numbers are unreadable otherwise and these get big. */
export function formatCount(n: number | null | undefined): string {
  if (n == null) return '—'
  return n.toLocaleString()
}

export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '—'
  return `${value.toFixed(decimals)}%`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toFixed(1)} ${units[unit]}`
}

/**
 * How a latency should read at a glance.
 *
 * Thresholds are deliberately absolute rather than relative to the current distribution. A relative
 * scale always has a worst endpoint painted red, which trains people to ignore red; these say
 * whether a number is actually bad, so a page of green means a page of green.
 */
export function latencySeverity(ms: number): 'success' | 'info' | 'warning' | 'error' {
  if (ms < 100) return 'success'
  if (ms < 500) return 'info'
  if (ms < 2000) return 'warning'
  return 'error'
}

export function errorRateSeverity(percent: number): 'success' | 'warning' | 'error' {
  if (percent < 0.5) return 'success'
  if (percent < 5) return 'warning'
  return 'error'
}

/** Local time for a UTC stamp. The API always sends an explicit zone, so this cannot drift. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString()
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString()
}

/** Axis labels: a time inside a day, a date beyond it. */
export function formatAxisTime(iso: string, hours: number): string {
  const date = new Date(iso)
  if (hours <= 48) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

/** The windows offered across the page. One list, so every tab agrees on what "24h" means. */
export const TIME_WINDOWS = [
  { label: '15 min', hours: 0.25 },
  { label: '1 hour', hours: 1 },
  { label: '6 hours', hours: 6 },
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
  { label: '14 days', hours: 336 },
] as const

export const PROBE_OUTCOME_LABEL: Record<number, string> = {
  0: 'Healthy',
  1: 'Degraded',
  2: 'Failed',
}

export const PROBE_OUTCOME_COLOR: Record<number, 'success' | 'warning' | 'error'> = {
  0: 'success',
  1: 'warning',
  2: 'error',
}
